interface ParsedAvailabilityMessage {
  beforeStore: string;
  storeName: string;
  afterStore: string;
  // Shown on its own line, after "Choose another store" when out of stock
  secondLine?: string;
  // Starts the second line, as a link to choose another store
  secondLineLink?: string;
}

export function parseAvailabilityMessage(
  message: string,
  locale = 'en'
): ParsedAvailabilityMessage | null {
  if (!message) return null;

  const [firstLine, secondLine] = message.split('\n');

  // "Out of stock at [STORE]." / "Rupture de stock à [STORE].", then "Choose
  // another store for pickup." on a second line. The store isn't a link here:
  // "Choose another store" is.
  const isOutOfStock = (
    locale === 'fr'
      ? /^Rupture\s+de\s+stock\s+à\s/i
      : /^Out\s+of\s+stock\s+at\s/i
  ).test(firstLine);
  if (isOutOfStock) {
    const chooseStoreMatch = secondLine?.match(
      locale === 'fr'
        ? /^(Choisissez\s+un\s+autre\s+magasin)\s*(.*)$/i
        : /^(Choose\s+another\s+store)\s*(.*)$/i
    );
    return {
      beforeStore: firstLine,
      storeName: '',
      afterStore: '',
      secondLine: chooseStoreMatch ? chooseStoreMatch[2] : secondLine,
      secondLineLink: chooseStoreMatch?.[1],
    };
  }

  // "Ramassage au magasin de [STORE] vendredi": next day pickup after the 3pm
  // cutoff, ready on a named day
  const frenchNamedDayMatch =
    locale === 'fr' &&
    message.match(
      /^(.+?\s+au\s+magasin\s+de)\s+(.+?)\s+(lundi|mardi|mercredi|jeudi|vendredi|samedi|dimanche)$/i
    );
  if (frenchNamedDayMatch) {
    return {
      beforeStore: frenchNamedDayMatch[1],
      storeName: frenchNamedDayMatch[2],
      afterStore: frenchNamedDayMatch[3],
    };
  }

  // English patterns
  const englishPatterns = [
    // "Pick up at the [STORE] store tomorrow when you order before 3PM"
    /^(.+?\s+at\s+the)\s+(.+?)\s+store\s+(.+?)\s+when\s+you\s+order\s+before\s+(.+)$/i,
    // "Ship to the [STORE] store for pickup between [DATES]" or legacy "Pick up at the … store between …"
    /^(.+?\s+(?:at|to)\s+the)\s+(.+?)\s+store\s+(.+)$/i,
  ];

  // French patterns
  const frenchPatterns = [
    // "À ramasser demain au magasin [STORE] si vous passez votre commande avant [TIME]"
    /^(.+?\s+demain\s+au\s+magasin)\s+(.+?)\s+si\s+vous\s+passez\s+votre\s+commande\s+avant\s+(.+)$/i,
    // "Livraison au magasin [STORE] pour un retrait entre le [DATES]" (cart.pickup.in.store.date.new)
    /^(.+?\s+au\s+magasin)\s+(.+?)\s+pour\s+un\s+retrait\s+entre\s+(.+)$/i,
    // Legacy: "À ramasser au magasin de [STORE] entre le [DATES]"
    /^(.+?\s+au\s+magasin\s+de)\s+(.+?)\s+entre\s+(.+)$/i,
  ];

  const patterns = locale === 'fr' ? frenchPatterns : englishPatterns;
  for (const pattern of patterns) {
    const match = message.match(pattern);

    if (match) {
      // Handle different pattern structures
      if (match.length === 5) {
        // English pattern with timing: "Pick up at the [STORE] store tomorrow when you order before 3PM"
        return {
          beforeStore: match?.[1]?.trim(),
          storeName: match?.[2]?.trim(),
          afterStore: `store ${match?.[3]?.trim()} when you order before ${match?.[4]?.trim()}`,
        };
      } else if (match.length === 4) {
        // Check if this is a French pattern or English pattern
        if (locale === 'fr') {
          if (match[1].includes('demain au magasin')) {
            // French "demain" pattern with timing: "À ramasser demain au magasin [STORE] si vous passez votre commande avant [TIME]"
            return {
              beforeStore: match?.[1]?.trim(),
              storeName: match?.[2]?.trim(),
              afterStore: `si vous passez votre commande avant ${match?.[3]?.trim()}`,
            };
          }
          if (match[1].includes('au magasin de')) {
            // Legacy: "À ramasser au magasin de [STORE] entre le [DATES]"
            return {
              beforeStore: match?.[1]?.trim(),
              storeName: match?.[2]?.trim(),
              afterStore: `entre ${match?.[3]?.trim()}`,
            };
          }
          // "Livraison au magasin [STORE] pour un retrait entre le [DATES]"
          return {
            beforeStore: match?.[1]?.trim(),
            storeName: match?.[2]?.trim(),
            afterStore: `pour un retrait entre ${match?.[3]?.trim()}`,
          };
        } else {
          // "Ship to the … store for pickup between …" or legacy "Pick up at the … store between …"
          return {
            beforeStore: match?.[1]?.trim(),
            storeName: match?.[2]?.trim(),
            afterStore: `store ${match?.[3]?.trim()}`,
          };
        }
      }
    }
  }

  // If no pattern matches, return the original message as beforeStore
  return {
    beforeStore: message,
    storeName: '',
    afterStore: '',
  };
}
